import React, { useState, useCallback, useEffect } from "react";
import {
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  ShareAltOutlined,
  SwapOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import { message, Modal, Button } from "antd";
import { connect } from "dva";
import { router } from "umi";
import { ShipmentStatus } from "@/utils/enum";
import { getUrlParams, DEFAULT_ID } from "@/utils/utils";
import MoreMenu from "@/components/MoreMenu";
import ShareMyRide from "@/components/ShareMyRide";

const { confirm } = Modal;

const checkCanShare = (configPerm, shipmentData) => {
  return (
    configPerm["CoreService.Shipments.Share"] &&
    [ShipmentStatus.Assigned, ShipmentStatus.Delivered].includes(shipmentData.status)
  );
};

const checkCanEdit = (configPerm, shipmentData) => {
  if (shipmentData.status >= ShipmentStatus.Delivered) {
    return false;
  }

  return configPerm["CoreService.Shipments.Edit"];
};

const checkCanDelete = (configPerm, shipmentData) => {
  if (shipmentData.status >= ShipmentStatus.Posted) {
    return false;
  }

  return configPerm["CoreService.Shipments.Delete"];
};

const checkCanCancel = (configPerm, shipmentData) => {
  if (shipmentData.onHold) {
    return false;
  }

  if (shipmentData.status !== ShipmentStatus.Posted) {
    return false;
  }

  return configPerm["CoreService.Shipments.Cancel"];
};

const checkCanCreateDealerExchange = (configPerm, shipmentData) => {
  if (
    (shipmentData.parentShipmentId !== null && shipmentData.parentShipmentId !== undefined) ||
    (shipmentData.childShipmentId !== null && shipmentData.childShipmentId !== undefined)
  ) {
    return false;
  }

  if (shipmentData.status >= ShipmentStatus.Posted) {
    return false;
  }

  return configPerm["CoreService.Shipments.Create"];
};

const checkShipmentHasVehiclesWithActiveQuotes = shipmentData => {
  if (shipmentData?.status === ShipmentStatus.Draft || shipmentData?.vehicles?.length === 0) return false;

  for (let vehicle of shipmentData.vehicles) {
    if (vehicle.quote.quoteId !== DEFAULT_ID) {
      return true;
    }
  }

  return false;
};

const handleDeleteShipment = (dispatch, shipmentData, confirmedDeleteExchange) => {
  const { id: shipmentId, childShipmentId, parentShipmentId } = shipmentData;

  if ((childShipmentId || parentShipmentId) && !confirmedDeleteExchange) {
    confirm({
      title: "Warning",
      content:
        "This shipment is part of a dealer exchange. If this shipment is deleted, the other shipment in the exchange will also be deleted. Are you sure?",
      onOk() {
        handleDeleteShipment(dispatch, shipmentData, true);
      },
    });
    return;
  }

  const shipmentHasVehiclesWithActiveQuotes = checkShipmentHasVehiclesWithActiveQuotes(shipmentData);

  if (shipmentHasVehiclesWithActiveQuotes) {
    // Need to collect reason from user for why they are deleting the shipment (and its associated quotes).
    dispatch({
      type: "shipmentDetails/toggleDeleteReasonModalVisible",
      payload: true,
    });

    return;
  }

  // Only need to show confirmation popup if above conditions aren't true
  confirm({
    title: "Delete Shipment?",
    content: "Are you sure you want to delete this shipment? This action cannot be undone.",
    onOk() {
      const hide = message.loading("Deleting");
      dispatch({
        type: "shipmentDetails/removeShipment",
        payload: { shipmentId },
      }).then(() => {
        hide();
        message.success("Shipment successfully deleted.");
        router.push(`/shipments/dealer/all`);
      });
    },
  });
};

const handleCancelShipment = (dispatch, shipmentData, confirmedCancelExchange) => {
  const { childShipmentId, parentShipmentId } = shipmentData;

  if ((childShipmentId || parentShipmentId) && !confirmedCancelExchange) {
    confirm({
      title: "Warning",
      content:
        "This shipment is part of a dealer exchange. If this shipment is canceled, the other shipment in the exchange will also be canceled. Are you sure?",
      onOk() {
        handleCancelShipment(dispatch, shipmentData, true);
      },
    });
    return;
  }

  dispatch({
    type: "shipmentDetails/toggleCancelModalVisible",
    payload: true,
  });
};

const checkCanRequestCancel = (configPerm, shipmentData) => {
  if (shipmentData.status <= ShipmentStatus.Posted) {
    return false;
  }

  if (shipmentData.onHold) {
    return false;
  }

  return configPerm["CoreService.Shipments.Cancel"];
};

const ShipmentActionsButton = ({ shipmentData, dispatch, configPerm }) => {
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [canShipmentActions, setCanShipmentActions] = useState(false);

  const canEdit = checkCanEdit(configPerm, shipmentData);
  const canDelete = checkCanDelete(configPerm, shipmentData);
  const canCancel = checkCanCancel(configPerm, shipmentData);
  const canShare = checkCanShare(configPerm, shipmentData);
  const canCreateDealerExchange = checkCanCreateDealerExchange(configPerm, shipmentData);

  const canRequestCancel = checkCanRequestCancel(configPerm, shipmentData);

  useEffect(() => {
    setCanShipmentActions(canEdit || canDelete || canCancel || canShare || canCreateDealerExchange || canRequestCancel);
  }, [shipmentData]);

  const onItemClick = useCallback(
    key => {
      const entityId = getUrlParams("entityId");
      switch (key) {
        case "edit":
          router.push(`/shipments/dealer/edit?shipmentId=${shipmentData.id}`);
          break;
        case "delete":
          handleDeleteShipment(dispatch, shipmentData, entityId);
          break;
        case "cancel":
          handleCancelShipment(dispatch, shipmentData);
          break;
        case "exchange":
          dispatch({
            type: "shipmentDetails/toggleDealerExchangeModalVisible",
            payload: true,
          });
          break;
        case "share":
          /**
           * ShareMyRide has a Modal child and it's inside a Menu.Item
           * component, this cause closing the Modal to trigger this callback
           * again, so we have one trigger when the user want's to open the
           * Modal and another when they want to close it.
           */
          if (!shareModalVisible) {
            setShareModalVisible(true);
          }
          break;
      }
    },
    [dispatch, shipmentData, shareModalVisible],
  );

  return (
    <>
      {canShipmentActions && (
        <MoreMenu
          onItemClick={onItemClick}
          items={[
            {
              key: "edit",
              icon: <EditOutlined />,
              label: "Edit",
              authority: canEdit,
            },
            {
              key: "delete",
              icon: <DeleteOutlined />,
              label: "Delete",
              authority: canDelete,
            },
            {
              key: "cancel",
              icon: <CloseCircleOutlined />,
              label: "Cancel",
              authority: canCancel,
            },
            {
              key: "cancel",
              icon: <CloseCircleOutlined />,
              label: "Request Cancel",
              authority: canRequestCancel,
            },
            {
              key: "exchange",
              icon: <SwapOutlined />,
              label: "Create Dealer Trade",
              authority: canCreateDealerExchange,
            },
            {
              key: "share",
              icon: <ShareAltOutlined />,
              authority: canShare,
              label: (
                <ShareMyRide
                  isMenuItem
                  shipmentId={shipmentData.id}
                  modalVisible={shareModalVisible}
                  setModalVisible={setShareModalVisible}
                />
              ),
            },
          ]}
        >
          <Button icon={<MenuOutlined />}>Shipment Options</Button>
        </MoreMenu>
      )}
    </>
  );
};

export default connect(({ shipmentDetails, configs }) => ({
  shipmentData: shipmentDetails.data,
  configPerm: configs.configPerm,
}))(ShipmentActionsButton);
